import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Users, UserCheck, Clock, Ban, CalendarX, IndianRupee, FileText, Star,
  MessageSquare, AlertTriangle, Send, TimerReset,
} from "lucide-react";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";
import api, { arr } from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import StatCard from "@/components/common/StatCard";
import ChartCard, { CHART_COLORS, chartTooltipStyle } from "@/components/common/ChartCard";
import StatusBadge from "@/components/common/StatusBadge";
import { CardSkeletonGrid } from "@/components/common/Loading";

export default function AdminDashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/admin/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) return <div className="space-y-6"><PageHeader title="Dashboard" /><CardSkeletonGrid /></div>;

  const { cards = {}, charts = {}, tables = {} } = data;
  charts.revenueChart = arr(charts.revenueChart);
  charts.clientGrowth = arr(charts.clientGrowth);
  charts.postsChart = arr(charts.postsChart);
  charts.planDistribution = arr(charts.planDistribution);
  tables.recentClients = arr(tables.recentClients);
  tables.expiringSoon = arr(tables.expiringSoon);
  tables.newSignups = arr(tables.newSignups);
  const kpis = [
    { label: "Total Clients", value: cards.totalClients, icon: Users, accent: "brand" },
    { label: "Active", value: cards.activeClients, icon: UserCheck, accent: "green" },
    { label: "Trial", value: cards.trialClients, icon: Clock, accent: "purple" },
    { label: "Suspended", value: cards.suspendedClients, icon: Ban, accent: "red" },
    { label: "Expired", value: cards.expiredClients, icon: CalendarX, accent: "amber" },
    { label: "Monthly Revenue", value: `₹${cards.monthlyRevenue.toLocaleString()}`, icon: IndianRupee, accent: "green" },
    { label: "Posts Published", value: cards.totalPostsPublished, icon: FileText, accent: "blue" },
    { label: "Reviews Synced", value: cards.totalReviewsSynced, icon: Star, accent: "amber" },
    { label: "Pending AI Replies", value: cards.pendingAiReplies, icon: MessageSquare, accent: "purple" },
    { label: "Failed Automations", value: cards.failedAutomations, icon: AlertTriangle, accent: "red" },
    { label: "WhatsApp Sent", value: cards.whatsappMessagesSent, icon: Send, accent: "green" },
    { label: "Expiring Soon", value: cards.clientsExpiringSoon, icon: TimerReset, accent: "amber" },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title="Super Admin Dashboard" subtitle="Overview of all clients, revenue, and platform activity" />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {kpis.map((k) => <StatCard key={k.label} {...k} testId={`admin-kpi-${k.label.toLowerCase().replace(/\s+/g, "-")}`} />)}
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
        <ChartCard title="Monthly Revenue" subtitle="Last 6 months">
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={charts.revenueChart}>
              <defs>
                <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#291d89" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#291d89" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip {...chartTooltipStyle()} />
              <Area type="monotone" dataKey="revenue" stroke="#291d89" strokeWidth={2.5} fill="url(#rev)" />
            </AreaChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Client Growth" subtitle="Cumulative clients">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={charts.clientGrowth}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip {...chartTooltipStyle()} />
              <Line type="monotone" dataKey="clients" stroke="#4e67eb" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Posts Published" subtitle="Per month">
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={charts.postsChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip {...chartTooltipStyle()} />
              <Bar dataKey="posts" fill="#4e67eb" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Plan Distribution" subtitle="Clients by plan">
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={charts.planDistribution} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3}>
                {arr(charts?.planDistribution).map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
              </Pie>
              <Tooltip {...chartTooltipStyle()} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <ChartCard title="New Sign-ups (Action Needed)">
          <div className="divide-y divide-slate-50">
            {tables.newSignups.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No new sign-ups</p>}
            {arr(tables?.newSignups).map((c) => (
              <button key={c.id} onClick={() => navigate(`/admin/clients/${c.id}`)} className="flex w-full items-center justify-between py-3 text-left hover:bg-slate-50">
                <div>
                  <p className="text-sm font-semibold text-slate-800">{c.businessName}</p>
                  <p className="text-xs text-slate-500">{c.email}</p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-slate-400">{new Date(c.createdAt).toLocaleDateString()}</p>
                  <span className="text-xs font-medium text-brand-600">Activate &rarr;</span>
                </div>
              </button>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Recent Clients">
          <div className="divide-y divide-slate-50">
            {tables.recentClients.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No clients yet</p>}
            {arr(tables?.recentClients).map((c) => (
              <button key={c.id} onClick={() => navigate(`/admin/clients/${c.id}`)} className="flex w-full items-center justify-between py-3 text-left hover:bg-slate-50">
                <div>
                  <p className="text-sm font-semibold text-slate-800">{c.businessName}</p>
                  <p className="text-xs text-slate-400">{c.plan}</p>
                </div>
                <StatusBadge status={c.status} />
              </button>
            ))}
          </div>
        </ChartCard>

        <ChartCard title="Clients Expiring Soon">
          <div className="divide-y divide-slate-50">
            {tables.expiringSoon.length === 0 && <p className="py-6 text-center text-sm text-slate-400">No upcoming expirations</p>}
            {arr(tables?.expiringSoon).map((c) => (
              <div key={c.id} className="flex items-center justify-between py-3">
                <p className="text-sm font-semibold text-slate-800">{c.businessName}</p>
                <p className="text-xs text-slate-500">{c.subscriptionEnd ? new Date(c.subscriptionEnd).toLocaleDateString() : "—"}</p>
              </div>
            ))}
          </div>
        </ChartCard>
      </div>
    </div>
  );
}
